import "es6-symbol/implement";
import axios from "axios";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import { User } from "../redux/actions/index";
import store from "../redux/store";
import { isExpiredSessionError } from "./session_expiry";
import { recordError, describeRequestError } from "./last_error";

const singleton = Symbol();
const singletonEnforcer = Symbol();

export default class Http {
  /**
   * @constructor
   */
  constructor(enforcer) {
    if (enforcer !== singletonEnforcer) {
      throw new Error("Cannot construct singleton");
    }
    this.client = axios.create({
      baseURL: process.env.SITE_URL,
      responseType: "json",
    });
    this.client.interceptors.request.use(
      (config) => {
        if (window.location.pathname !== "/jobs") {
          store.dispatch(showLoading());
        }
        return config;
      },
      (error) => {
        store.dispatch(hideLoading());
        return Promise.reject(error);
      }
    );
    this.client.interceptors.response.use(
      (response) => {
        store.dispatch(hideLoading());
        return response;
      },
      (error) => {
        /**
         * @todo
         * implement token refresh when expired
         */
        if (isExpiredSessionError(error)) {
          //If token expired
          store.dispatch(User.logout());
          // `sharedInstance` is a *static* getter, so `this.sharedInstance` is
          // undefined here (`this` is the instance) and this line threw a
          // TypeError on every 401 — before the reload below ever ran. The app
          // was left half-logged-out: store cleared, stale Authorization still
          // on the axios defaults, page never reloaded, so every following
          // request 401'd and threw again. The two other call sites in the app
          // (header.js, sidebar.js) go through `Http.sharedInstance`; from in
          // here it is just an instance method.
          this.resetAxiosHeaders();
          window.location.reload();
        }
        // Every api/*.js wrapper turns a rejection into `null`/`[]`/`{success:
        // false}` and throws the reason away, so a 5xx or a dropped connection
        // reached the UI as "empty" with no trace anywhere. Record the server's
        // own message here, the one place every request already passes through.
        if (!error.response || error.response.status >= 500) {
          const described = describeRequestError(error);
          recordError(error, {
            name: described.name,
            message: described.message,
            status: described.status,
            path: described.path,
            requestId: described.requestId,
            source: "http",
          });
        }

        store.dispatch(hideLoading());
        return Promise.reject(error);
      }
    );
  }

  resetAxiosHeaders() {
    delete axios.defaults.headers.common["Authorization"];
  }

  /**
   * Singleton instance caller
   * @return {Http}
   */
  static get sharedInstance() {
    if (!this[singleton]) {
      this[singleton] = new Http(singletonEnforcer);
    }
    return this[singleton];
  }

  /**
   * Returns axios instance
   * @return {axios}
   */
  getClient() {
    return this.client;
  }

  /**
   * Applys auth headers to axios
   * @retun {axios}
   */
  withAuth() {
    // SAM-145. `Http` is a singleton and this is called per request from 174
    // call sites, so registering unconditionally added one more interceptor to
    // the same axios client on every single request — an unbounded list that
    // grew for the life of the tab, each entry re-stamping the same header.
    // Registering once is enough: the handler reads the store at request time,
    // so it always sees the current token.
    if (this.authInterceptorId === undefined) {
      this.authInterceptorId = this.client.interceptors.request.use(
        (config) => {
          const state = store.getState();
          const token =
            state.user && state.user.access_token
              ? state.user.access_token.accessToken
              : null;
          // Send no header at all rather than a `Bearer undefined` the API
          // cannot honour. That string was indistinguishable from a real
          // credential to everything downstream — including
          // isExpiredSessionError, which is how an anonymous 401 on the public
          // questionnaire became a logout and a page reload (SAM-145). It also
          // leaked onto getClient(), the deliberately token-free pre-login
          // path, because both share this one axios instance.
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          return config;
        },
        (error) => Promise.reject(error)
      );
    }
    return this.client;
  }

  /**
   * Download file from the server and trigger download in the browser
   * @param {string} url - The API endpoint for file download
   * @param {string} filename - The name for the downloaded file
   * @return {Promise<void>}
   */
  async downloadFile(url, filename) {
    try {
      store.dispatch(showLoading());

      // Make the request for file download
      const response = await this.client.get(url, {
        responseType: "blob", // Ensure response is a Blob (binary)
      });

      // Create a URL for the Blob
      const fileURL = window.URL.createObjectURL(new Blob([response.data]));

      // Create a temporary link element
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", filename); // Set the file name for download

      // Append to the body and trigger the download
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.remove();
      window.URL.revokeObjectURL(fileURL);

      store.dispatch(hideLoading());
    } catch (error) {
      store.dispatch(hideLoading());
      console.error("Error downloading file:", error);
    }
  }
}
